import React, { useEffect, useMemo, useRef, useState } from 'react';

const INPUT_ID = 'tc-copy-search';
const LIST_ID = 'tc-copy-list';
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function formatDate(row) {
  const value = String(row.tradeDate || row.valueDatetime || '').slice(0, 10);
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return value || 'no trade date';
  return `${match[3]} ${MONTHS[Number(match[2]) - 1]} ${match[1]}`;
}

function formatNotional(row) {
  if (row.notionalAmount === null || row.notionalAmount === undefined || row.notionalAmount === '') {
    return '';
  }
  const amount = Number(row.notionalAmount);
  const text = Number.isFinite(amount)
    ? amount.toLocaleString('en-US', { maximumFractionDigits: 8 })
    : String(row.notionalAmount);
  return row.notionalAmountCcy ? `${text} ${row.notionalAmountCcy}` : text;
}

// The server sends the underlyings as a JSON array string. Old trades can have none.
function formatUnderlyings(row) {
  let value = row.underlyings;
  if (typeof value === 'string') {
    try {
      value = JSON.parse(value);
    } catch (error) {
      return value;
    }
  }
  return Array.isArray(value) ? value.filter(Boolean).join(', ') : '';
}

function describe(row) {
  return {
    id: String(row.transactionId),
    counterparty: row.clientName || 'Unknown counterparty',
    notional: formatNotional(row),
    reference: row.referenceNumber || 'no reference',
    underlyings: formatUnderlyings(row),
    date: formatDate(row),
  };
}

function matches(view, row, query) {
  if (!query) return true;
  const needle = query.toLowerCase();
  return [
    view.counterparty,
    view.notional,
    view.reference,
    view.underlyings,
    view.id,
    row.accountNumber,
    row.notionalAmount === null || row.notionalAmount === undefined ? '' : String(row.notionalAmount),
  ].some((value) => String(value || '').toLowerCase().includes(needle));
}

function Highlight({ text, query }) {
  const value = String(text || '');
  const at = query ? value.toLowerCase().indexOf(query.toLowerCase()) : -1;
  if (at < 0) return value;
  return (
    <>
      {value.slice(0, at)}
      <mark>{value.slice(at, at + query.length)}</mark>
      {value.slice(at + query.length)}
    </>
  );
}

/**
 * Lets an agent who manages a client fill this booking from an existing counterparty trade.
 * The caller renders it only for such a session. The server makes the same check.
 */
export default function CopySourceSearch({
  source,
  unmatchedFields,
  fieldLabel,
  sources,
  sourcesError,
  sourcesLoading,
  fieldsError,
  onSearchStart,
  onChoose,
  onClear,
}) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  // The copied trade the agent chose to replace. A new pick changes the source, so the
  // search closes again without an extra state reset.
  const [changingFrom, setChangingFrom] = useState(null);
  const inputRef = useRef(null);
  const changing = !!source && changingFrom === source.transactionId;

  useEffect(() => {
    if (changing && inputRef.current) inputRef.current.focus();
  }, [changing]);

  const shown = useMemo(() => {
    const trimmed = query.trim();
    return (sources || [])
      .map((row) => ({ row, view: describe(row) }))
      .filter(({ row, view }) => matches(view, row, trimmed));
  }, [sources, query]);

  const safeActive = Math.min(activeIndex, Math.max(shown.length - 1, 0));

  if (source && !changing) {
    const view = describe(source);
    const gaps = unmatchedFields || [];
    return (
      <div className="tc-copy tc-copy--done" role="status" aria-label="Copied into the form">
        <div className="tc-copy__done-row">
          <div>
            <div className="tc-copy__done-label">Copied into the form</div>
            <div className="tc-copy__done-title">
              {view.counterparty}
              {view.notional ? ` · ${view.notional}` : ''}
            </div>
            <div className="tc-copy__meta">
              {view.reference} · traded {view.date} · #{view.id}
            </div>
          </div>
          <div className="tc-copy__actions">
            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setChangingFrom(source.transactionId)}>
              Choose another
            </button>
            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={onClear}>
              Clear copy
            </button>
          </div>
        </div>
        {gaps.length ? (
          <div className="tc-copy__gaps">
            <span className="tc-copy__gaps-label">Fill in by hand:</span>
            {gaps.map((key) => (
              <span key={key} className="tc-copy__gap" data-field-key={key}>
                {fieldLabel ? fieldLabel(key) : key}
              </span>
            ))}
          </div>
        ) : null}
      </div>
    );
  }

  const choose = (item) => {
    if (!item) return;
    setOpen(false);
    setQuery('');
    setChangingFrom(null);
    onChoose(item.row);
  };

  const onKeyDown = (event) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setOpen(true);
      setActiveIndex(Math.min(safeActive + 1, shown.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex(Math.max(safeActive - 1, 0));
    } else if (event.key === 'Enter') {
      if (open && shown[safeActive]) {
        event.preventDefault();
        choose(shown[safeActive]);
      }
    } else if (event.key === 'Escape') {
      setOpen(false);
      setChangingFrom(null);
    }
  };

  const total = sources ? sources.length : 0;
  const loading = sourcesLoading && !sources;
  const trimmed = query.trim();

  return (
    <div className="tc-copy form-group">
      <div className="tc-copy__head">
        <label htmlFor={INPUT_ID}>Copy from an existing trade</label>
        {sources ? (
          <span className="tc-copy__hint">
            {total} active {total === 1 ? 'trade' : 'trades'}
          </span>
        ) : null}
      </div>
      <div className={`tc-copy__combo${open ? ' is-open' : ''}`}>
        <svg className="tc-copy__icon" viewBox="0 0 24 24" aria-hidden="true">
          <circle cx="11" cy="11" r="7" />
          <path d="m20 20-3.5-3.5" />
        </svg>
        <input
          ref={inputRef}
          id={INPUT_ID}
          type="text"
          className="form-control"
          role="combobox"
          autoComplete="off"
          aria-autocomplete="list"
          aria-expanded={open}
          aria-controls={LIST_ID}
          aria-activedescendant={open && shown[safeActive] ? `${LIST_ID}-${shown[safeActive].view.id}` : undefined}
          placeholder="Search by counterparty, reference, ID or underlying"
          value={query}
          onFocus={() => {
            setOpen(true);
            onSearchStart();
          }}
          onBlur={() => setOpen(false)}
          onChange={(event) => {
            setQuery(event.target.value);
            setActiveIndex(0);
            setOpen(true);
          }}
          onKeyDown={onKeyDown}
        />
        {open ? (
          <div className="tc-copy__list" id={LIST_ID} role="listbox" aria-label="Existing trades">
            {loading ? <div className="tc-copy__note">Loading existing trades…</div> : null}
            {!loading && sources && !total ? (
              <div className="tc-copy__note">No active counterparty trade of this product exists yet.</div>
            ) : null}
            {!loading && total > 0 ? (
              <div className="tc-copy__count">
                {shown.length} of {total} · newest first
              </div>
            ) : null}
            {!loading && total > 0 && !shown.length ? (
              <div className="tc-copy__note">No active trade matches "{trimmed}".</div>
            ) : null}
            {!loading
              ? shown.map((item, index) => (
                  <div
                    key={item.view.id}
                    id={`${LIST_ID}-${item.view.id}`}
                    role="option"
                    aria-selected={index === safeActive}
                    className={`tc-copy__option${index === safeActive ? ' is-active' : ''}`}
                    onMouseEnter={() => setActiveIndex(index)}
                    onMouseDown={(event) => {
                      event.preventDefault();
                      choose(item);
                    }}
                  >
                    <span className="tc-copy__cp">
                      <Highlight text={item.view.counterparty} query={trimmed} />
                    </span>
                    <span className="tc-copy__amount">
                      <Highlight text={item.view.notional} query={trimmed} />
                    </span>
                    <span className="tc-copy__meta">
                      <Highlight text={item.view.reference} query={trimmed} />
                      {item.view.underlyings ? (
                        <>
                          {' · '}
                          <Highlight text={item.view.underlyings} query={trimmed} />
                        </>
                      ) : null}
                    </span>
                    <span className="tc-copy__meta tc-copy__date">
                      {item.view.date} · #<Highlight text={item.view.id} query={trimmed} />
                    </span>
                  </div>
                ))
              : null}
          </div>
        ) : null}
      </div>
      {sourcesError ? (
        <div className="alert alert-danger tc-copy__alert" role="alert">
          {sourcesError}
        </div>
      ) : null}
      {fieldsError ? (
        <div className="alert alert-danger tc-copy__alert" role="alert">
          {fieldsError}
        </div>
      ) : null}
      {source ? (
        <button type="button" className="btn btn-link tc-copy__keep" onClick={() => setChangingFrom(null)}>
          Keep current copy
        </button>
      ) : null}
    </div>
  );
}
